import { type CompetitorListItem, PARTICIPANTS_PAGE_SIZE_DEFAULT } from "@seagames/contracts";
import { useFilterDraft } from "@seagames/ui";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { PAGE_SIZE_CHOICES, pageBeyondEnd, pageToUrl, sizeToUrl } from "../../components/nocs/list-params";
import { nocOptionsQuery } from "../../components/nocs/queries";
import { useOnGamesSwitch, useScrollToTopOnPage } from "../../components/nocs/use-list-page";
import { useOpenOnSessionNoc } from "../../components/nocs/use-session-noc";
import { PageHeader } from "../../components/page/page-header";
import { AddEntryDrawer } from "../../components/teams/add-entry/add-entry-drawer";
import { EntriesTable } from "../../components/teams/entries-table";
import { competitorsListQuery } from "../../components/teams/queries";
import {
	EMPTY_TEAMS_DRAFT,
	type TeamsDraft,
	type TeamsSearch,
	teamsDraftFrom,
	teamsFilterCount,
	teamsPaging,
	teamsParams,
	teamsSearch,
	teamsSearchFrom,
} from "../../components/teams/search";
import { TeamsFilters } from "../../components/teams/teams-filters";
import { Button } from "../../components/ui/button";
import { EmptyState } from "../../components/ui/empty-state";
import { Spinner } from "../../components/ui/spinner";
import { errorMessage } from "../../lib/api/messages";
import { gamesSportsQuery, useCurrentGames } from "../../lib/games";
import { formatInteger } from "../../lib/i18n/format";

/**
 * Participants › Teams & entries — every entry of the current games, team or
 * individual, one page at a time (`GET /competitors`), in programme order.
 * Write roles add an entry here ("Add entry": event, NOC, roster); rosters
 * are changed in a meet's start list. An NOC login opens on its own NOC,
 * like Athletes.
 */
export const Route = createFileRoute("/_app/teams/")({
	validateSearch: teamsSearch,
	component: TeamsPage,
});

function TeamsPage() {
	const { t } = useTranslation("teams");
	const search = Route.useSearch();
	const { session, readOnly } = Route.useRouteContext();
	const [adding, setAdding] = useState(false);
	const navigate = useNavigate({ from: "/teams/" });
	const current = useCurrentGames();
	const gamesId = current.gamesId;

	const sports = useQuery({ ...gamesSportsQuery(gamesId ?? ""), enabled: gamesId !== null });
	const nocs = useQuery({ ...nocOptionsQuery(gamesId ?? ""), enabled: gamesId !== null });

	const go = useCallback((next: TeamsSearch, replace = false) => void navigate({ search: next, replace }), [navigate]);
	const applied = useMemo(() => teamsDraftFrom(search), [search]);
	const onApply = useCallback((d: TeamsDraft) => go(teamsSearchFrom(d, { size: search.size })), [go, search.size]);
	const f = useFilterDraft<TeamsDraft>(applied, onApply, EMPTY_TEAMS_DRAFT);
	const filterCount = teamsFilterCount(search);

	const ready = useOpenOnSessionNoc({
		session,
		urlHasFilters: filterCount > 0,
		gamesNocIds: nocs.isError ? [] : nocs.data?.items.map((n) => n.responsible_organization_id),
		apply: (noc) => navigate({ search: { ...search, noc, page: undefined }, replace: true }),
	});

	// The sidebar switched games: sports, disciplines and events belong to the old one.
	useOnGamesSwitch(gamesId, () => go({ ...search, sport: undefined, discipline: undefined, event: undefined, page: undefined }, true));

	const params = gamesId !== null && ready ? teamsParams(gamesId, search) : null;
	const list = useQuery({
		...competitorsListQuery(params ?? { games_id: "", page: 1, page_size: PARTICIPANTS_PAGE_SIZE_DEFAULT }),
		enabled: params !== null,
		placeholderData: keepPreviousData,
	});

	const { page, pageSize } = teamsPaging(search);
	const beyond = list.data && list.data.items.length === 0 ? pageBeyondEnd(page, list.data.total, pageSize) : null;
	useEffect(() => {
		if (beyond !== null) go({ ...search, page: pageToUrl(beyond) }, true);
	}, [beyond]);

	const top = useRef<HTMLDivElement>(null);
	useScrollToTopOnPage(top, page);

	const open = (c: CompetitorListItem) => void navigate({ to: "/teams/$competitorId", params: { competitorId: c.competitor_id } });

	if (current.error) {
		return (
			<div className="flex flex-col">
				<PageHeader title={t("title")} />
				<EmptyState title={t("errors.gamesTitle")} body={errorMessage(current.error)} />
			</div>
		);
	}
	if (current.isLoading) return <Spinner className="size-[1.5rem] text-board" />;
	if (!current.games || gamesId === null) {
		return (
			<div className="flex flex-col">
				<PageHeader title={t("title")} />
				<EmptyState title={t("empty.noGamesTitle")} body={t("empty.noGamesBody")} />
			</div>
		);
	}

	const total = list.data?.total ?? 0;

	return (
		<div className="flex flex-col">
			<PageHeader
				title={t("title")}
				subtitle={t("subtitle", { games: current.games.name })}
				actions={
					!readOnly ? (
						<Button size="sm" onClick={() => setAdding(true)}>
							{t("addEntry.button")}
						</Button>
					) : undefined
				}
			/>

			{!readOnly && adding && (
				<AddEntryDrawer
					gamesId={gamesId}
					sports={sports.data ?? []}
					nocs={nocs.data?.items ?? []}
					// Start where the list is filtered: the sport, the event, the NOC.
					start={{ sportId: search.sport ?? "", eventId: search.sport ? (search.event ?? "") : "", nocId: search.noc ?? "" }}
					onClose={() => setAdding(false)}
				/>
			)}

			<div className="flex flex-col gap-[1rem]">
				<TeamsFilters
					gamesId={gamesId}
					draft={f.draft}
					set={f.set}
					onApply={f.apply}
					onReset={f.reset}
					activeCount={filterCount}
					sports={sports.data ?? []}
					nocs={nocs.data?.items ?? []}
				/>

				<div ref={top} className="scroll-mt-[4.5rem]">
					<EntriesTable
						storageKey="ovr.teams"
						rows={list.data?.items ?? []}
						loading={!ready || list.isPending || (list.isFetching && list.isPlaceholderData)}
						error={list.isError ? { message: errorMessage(list.error), onRetry: () => void list.refetch() } : null}
						empty={
							filterCount > 0
								? {
										title: t("empty.filteredTitle"),
										description: t("empty.filteredBody"),
										action: (
											<Button variant="outline" size="sm" onClick={f.reset}>
												{t("empty.reset")}
											</Button>
										),
									}
								: { title: t("empty.noneTitle", { games: current.games.name }), description: t("empty.noneBody") }
						}
						toolbar={
							list.data ? (
								<span className="text-[0.8125rem] text-muted" aria-live="polite">
									{t("table.count", { count: total, n: formatInteger(total) })}
								</span>
							) : null
						}
						pagination={
							list.data && total > 0
								? {
										page,
										pageSize,
										total,
										onPageChange: (p) => go({ ...search, page: pageToUrl(p) }),
										onPageSizeChange: (s) => go({ ...search, size: sizeToUrl(s, PARTICIPANTS_PAGE_SIZE_DEFAULT), page: undefined }),
										pageSizeOptions: PAGE_SIZE_CHOICES,
									}
								: undefined
						}
						onOpen={open}
					/>
				</div>
			</div>
		</div>
	);
}
